import {
  createContext,
  createSignal,
  createUniqueId,
  onCleanup,
  onMount,
  Show,
  useContext,
  splitProps,
  type Accessor,
  type ComponentProps,
  type ParentProps,
  For,
  type JSX,
  untrack,
} from "solid-js";
import {
  Checkbox,
  CheckboxGroup,
  CheckboxGroupLabel,
  ColorRadio,
  CheckboxGroupOption,
  CheckboxLabel,
  Input,
  MoneyInput,
  Textarea,
  Radio,
  RadioLabel,
  RadioGroup,
  RadioGroupLabel,
  RadioGroupOption,
} from "~/common/input";
import { Select } from "~/common/select";
import { Toggle } from "~/common/toggle";
import { Combobox, type ComboboxProps } from "~/common/combobox";
import { TimeInput } from "~/common/time_input";
import { DateInput } from "~/common/date_input";
import {
  type ExistingFile,
  ExistingFiles,
  FileDropzone,
  SelectedFiles,
} from "~/common/file_dropzone";
import clsx from "~/util/html/style";
import { hexToLch, lchToHex } from "~/util/lch";
import { Tooltip } from "./tooltip";
import { Icon } from "~/icon";
import type { FormDataErrors, InertiaForm } from "~/inertia";
import { Button } from "./button";

export type FieldError = string | undefined | null;

type FieldWidth = "fit" | "full" | "unset";
type AdditionalActionPosition = "end" | "inline";

type FormContext = {
  fieldWidth: FieldWidth;
  compact?: boolean;
  id: string;
  errorId: Accessor<string | undefined>;
  setErrorId: (id: string | undefined) => void;
  editable: boolean;
};

const FormContext = createContext<FormContext>();

function useForm() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useForm must be used within a Form");
  }
  return context;
}

type FieldContext = {
  idBase: string;
  errorId: Accessor<string | undefined>;
  setErrorId: (id: string | undefined) => void;
};

const FieldContext = createContext<FieldContext>();

export function useField() {
  const context = useContext(FieldContext);
  if (!context) {
    throw new Error("useField must be used within a FormField");
  }
  return context;
}

export function Form(
  props: ComponentProps<"form"> & {
    fieldWidth?: FieldWidth;
    errors?: FieldError[] | undefined;
    editable?: boolean;
  },
) {
  const [internalProps, formProps] = splitProps(props, [
    "fieldWidth",
    "id",
    "errors",
    "children",
    "editable",
  ]);
  const [errorId, setErrorId] = createSignal<string | undefined>();
  const id = untrack(() => internalProps.id ?? createUniqueId());

  return (
    <FormContext.Provider
      value={{
        id,
        get fieldWidth() {
          return internalProps.fieldWidth ?? "full";
        },
        get editable() {
          return internalProps.editable ?? true;
        },
        errorId,
        setErrorId,
      }}
    >
      <form
        {...formProps}
        class={clsx("flex flex-col gap-4", formProps.class)}
        aria-describedby={errorId()}
        id={id}
      >
        <BaseErrors errors={internalProps.errors} />
        {internalProps.children}
      </form>
    </FormContext.Provider>
  );
}

function Field(props: ParentProps<{ class?: string }>) {
  const idBase = createUniqueId();
  const [errorId, setErrorId] = createSignal<string | undefined>();

  return (
    <FieldContext.Provider value={{ idBase, errorId, setErrorId }}>
      <div role="group" class={props.class}>
        {props.children}
      </div>
    </FieldContext.Provider>
  );
}

export function makeInputId(idBase: string) {
  return `${idBase}-input`;
}

export function LabelTooltip(props: { tooltip: JSX.Element }) {
  return (
    <Tooltip content={props.tooltip}>
      {(triggerProps) => <Icon name="info" size="sm" {...triggerProps} />}
    </Tooltip>
  );
}

// `size="sm"` is the dense key/value label (a read-only page with many fields to a row).
export function LabelText(props: ParentProps<{ tooltip?: JSX.Element; size?: "sm" }>) {
  return (
    <p class={clsx(props.size === "sm" ? LABEL_CLASS_SM : LABEL_CLASS, "flex items-center gap-1")}>
      {props.children}
      <Show when={props.tooltip}>
        <LabelTooltip tooltip={props.tooltip} />
      </Show>
    </p>
  );
}

export const LABEL_CLASS = "text-sm leading-5 font-medium text-typography-secondary";
const LABEL_CLASS_SM = "text-xs leading-4 font-medium text-typography-secondary";

function FieldLabel(props: ParentProps<{ tooltip?: JSX.Element; required?: boolean }>) {
  const field = useField();

  return (
    <label
      for={makeInputId(field.idBase)}
      // the mb-0 is to override the global form margin-bottom of mb-1, probably should remove the global
      class={clsx(LABEL_CLASS, "mb-0 flex items-center gap-1")}
    >
      {props.children}
      <Show when={props.required}>
        <span>*</span>
      </Show>
      <Show when={props.tooltip}>
        <LabelTooltip tooltip={props.tooltip} />
      </Show>
    </label>
  );
}

export function FieldErrors(props: ParentProps) {
  const field = useField();
  const id = `${field.idBase}-errors`;

  onMount(() => {
    field.setErrorId(id);
  });

  onCleanup(() => {
    field.setErrorId(undefined);
  });

  return (
    <ul id={id} class="mt-1 flex flex-col gap-1">
      {props.children}
    </ul>
  );
}

// How an error reads where it is said inline, beside the thing it is about: a
// field's, or a write some other control could not make. The list a field's
// errors live in is `FieldErrors`; this is only the look.
export function InlineError(props: ParentProps) {
  return <span class="text-sm text-brand-danger italic">{props.children}</span>;
}

export function FieldError(props: ParentProps) {
  return (
    <li data-form-error-marker="true">
      <InlineError>{props.children}</InlineError>
    </li>
  );
}

export function BaseErrors(props: { errors: FieldError[] | undefined }) {
  const form = useForm();
  const id = `${form.id}-errors`;

  onMount(() => {
    form.setErrorId(id);
  });

  onCleanup(() => {
    form.setErrorId(undefined);
  });

  return (
    <Show when={props.errors && props.errors.length > 0}>
      <ul id={id} class="mt-1 flex flex-col gap-1">
        <For each={props.errors}>{(error) => <FieldError>{error}</FieldError>}</For>
      </ul>
    </Show>
  );
}

type FieldRootProps = {
  rootClass?: string;
  additionalActionPosition?: AdditionalActionPosition;
  label?: string;
  /** Drawn before the label's text, such as the dot of the colour the field's money is shown in. */
  labelPrefix?: JSX.Element;
  description?: string;
  tooltip?: JSX.Element;
  required?: boolean;
  additionalAction?: JSX.Element;
  errors?: FieldError[] | undefined;
  // Shown under the field without blocking the save.
  warning?: string | undefined;
  // Marks a field whose value is needed for the area to count as complete (but not to save): an
  // amber ring on the control + a "Needed to complete" hint. Non-blocking — the form still saves.
  needed?: boolean;
};

type WithFieldRootProps<T> = FieldRootProps & T;

// the amber ring applied to a `needed` (needed-for-complete) input/select
const NEEDED_RING = "ring-1 ring-brand-warning";

function splitFieldRootProps<T extends FieldRootProps>(props: T) {
  const [fieldRootProps, elementProps] = splitProps(props, [
    "rootClass",
    "label",
    "labelPrefix",
    "description",
    "tooltip",
    "required",
    "additionalAction",
    "additionalActionPosition",
    "errors",
    "warning",
    "needed",
  ]);
  return [fieldRootProps, elementProps] as const;
}

export function FieldRoot(props: ParentProps<FieldRootProps>) {
  return (
    <Field class={props.rootClass}>
      <Show when={props.label || props.additionalAction} fallback={null}>
        <div
          class={clsx(
            "mb-2 flex items-center",
            props.additionalActionPosition === "inline" ? "justify-start gap-2" : "justify-between",
          )}
        >
          <Show when={props.label}>
            <FieldLabel tooltip={props.tooltip} required={props.required}>
              {props.labelPrefix}
              {props.label}
            </FieldLabel>
          </Show>
          {props.additionalAction}
        </div>
      </Show>

      <Show when={props.description}>
        <p class="-mt-1 mb-2 text-xs text-typography-tertiary">{props.description}</p>
      </Show>

      {props.children}

      <Show when={props.warning}>
        <p class="mt-1 text-sm text-brand-warning-dark italic">{props.warning}</p>
      </Show>

      <Show when={props.needed && !props.errors}>
        <p class="mt-1 text-xs text-brand-warning-dark">Needed to complete</p>
      </Show>

      <Show when={props.errors}>
        <FieldErrors>
          <For each={props.errors}>{(error) => <FieldError>{error}</FieldError>}</For>
        </FieldErrors>
      </Show>
    </Field>
  );
}

// Columns are content-sized by default (a fit-width grid). `fill` names the column index(es) that
// should instead grow to absorb the remaining space; doing so stretches the grid full-width, so no
// separate width flag is needed.
export function FormGrid(props: ParentProps<{ cols: number; fill?: number | number[] }>) {
  const fillColumns = () => {
    const fill = props.fill ?? [];
    return new Set(Array.isArray(fill) ? fill : [fill]);
  };
  const template = () => {
    const fill = fillColumns();
    return Array.from({ length: props.cols }, (_, i) => (fill.has(i) ? "minmax(0, 1fr)" : "auto"));
  };

  return (
    <div
      style={{ "grid-template-columns": template().join(" ") }}
      class={clsx("grid gap-x-6 gap-y-4", fillColumns().size > 0 ? "w-full" : "w-fit")}
    >
      {props.children}
    </div>
  );
}

export function FormGridHeaderRow(props: ParentProps) {
  return (
    <div class={clsx("col-span-full -my-1 grid grid-cols-subgrid", LABEL_CLASS)}>
      {props.children}
    </div>
  );
}

export function FormGridRow(props: ParentProps) {
  return (
    <div class="col-span-full -mx-3 -my-2 grid grid-cols-subgrid rounded-lg px-3 py-2 hover:bg-brand-hover">
      {props.children}
    </div>
  );
}

export function FormGridRowAction(props: ParentProps) {
  // TODO: handle adjusting vertical alignment if a row has labels (need to shift action lower to line up w/ the inputs)
  return <div class={clsx("flex h-input w-fit items-center self-start")}>{props.children}</div>;
}

export function FormGridAddButton(
  props: Pick<ComponentProps<typeof Button>, "onClick" | "children">,
) {
  return <Button {...props} variant="plain" width="fit" leftIconName="plus" />;
}

/// Checkbox

function FieldCheckbox(
  _props: Omit<ComponentProps<typeof Checkbox>, "id" | "children"> & {
    label: string;
    tooltip?: JSX.Element;
  },
) {
  const [props, checkboxProps] = splitProps(_props, ["label", "tooltip"]);
  const field = useField();
  const id = makeInputId(field.idBase);

  return (
    <Checkbox id={id} {...checkboxProps}>
      <CheckboxLabel for={id}>{props.label}</CheckboxLabel>
      <Show when={props.tooltip}>
        <LabelTooltip tooltip={props.tooltip} />
      </Show>
    </Checkbox>
  );
}

export function CheckboxField(
  _props: Omit<ComponentProps<typeof FieldCheckbox>, "id"> & {
    label: string;
    tooltip?: JSX.Element;
    description?: string;
    checked: boolean;
    errors: FieldError[] | undefined;
  },
) {
  const [props, checkboxProps] = splitProps(_props, ["description"]);
  return (
    <Field>
      <FieldCheckbox {...checkboxProps} />

      <Show when={props.description}>
        {/* ml-7 = checkbox width + its gap-3, so the text sits under the label */}
        <p class="ml-7 text-xs text-typography-tertiary">{props.description}</p>
      </Show>

      <Show when={checkboxProps.errors}>
        <FieldErrors>
          <For each={checkboxProps.errors}>{(error) => <FieldError>{error}</FieldError>}</For>
        </FieldErrors>
      </Show>
    </Field>
  );
}

/// CheckboxGroup

export function getArrayFromChecked<T>(array: T[], value: T, checked: boolean): T[] {
  return checked ? [...array, value] : array.filter((id) => id !== value);
}

export function CheckboxGroupField(
  props: WithFieldRootProps<Omit<ComponentProps<typeof CheckboxGroup>, "label">>,
) {
  const [fieldRootProps, checkboxGroupProps] = splitFieldRootProps(props);

  return (
    <FieldRoot {...fieldRootProps}>
      <CheckboxGroup
        label={groupFieldLabel(CheckboxGroupLabel, fieldRootProps.label, fieldRootProps.required)}
        {...checkboxGroupProps}
      />
    </FieldRoot>
  );
}

export function CheckboxGroupFieldOption(props: ComponentProps<typeof CheckboxGroupOption>) {
  // TODO: use form/field context
  return <CheckboxGroupOption {...props} />;
}

/// Input

function FieldInput(
  _props: Omit<ComponentProps<typeof Input>, "id"> & {
    width?: FieldWidth;
  },
) {
  const [props, inputProps] = splitProps(_props, ["width"]);
  const field = useField();
  const form = useForm();
  const width = () => props.width ?? form.fieldWidth;

  return (
    <Input
      id={makeInputId(field.idBase)}
      width={width()}
      aria-describedby={field.errorId()}
      aria-invalid={Boolean(field.errorId())}
      {...(inputProps as ComponentProps<typeof Input>)}
    />
  );
}

export function InputField(
  props: WithFieldRootProps<ComponentProps<typeof FieldInput> & { suffix?: string }>,
) {
  const [fieldRootProps, rest] = splitFieldRootProps(props);
  const [local, elementProps] = splitProps(rest, ["suffix"]);
  const inputClass = () => clsx(elementProps.class, fieldRootProps.needed && NEEDED_RING);
  return (
    <FieldRoot {...fieldRootProps}>
      <Show when={local.suffix} fallback={<FieldInput {...elementProps} class={inputClass()} />}>
        <div class="flex items-center gap-2">
          <FieldInput {...elementProps} class={inputClass()} />
          <span class="text-sm text-typography-secondary">{local.suffix}</span>
        </div>
      </Show>
    </FieldRoot>
  );
}

/// Money

function FieldMoney(
  _props: Omit<ComponentProps<typeof MoneyInput>, "id"> & {
    width?: FieldWidth;
  },
) {
  const [props, inputProps] = splitProps(_props, ["width"]);
  const field = useField();
  const width = () => props.width ?? "fit";

  return (
    <MoneyInput
      id={makeInputId(field.idBase)}
      width={width()}
      aria-describedby={field.errorId()}
      aria-invalid={Boolean(field.errorId())}
      {...inputProps}
    />
  );
}

export function MoneyField(props: WithFieldRootProps<ComponentProps<typeof FieldMoney>>) {
  const [fieldRootProps, elementProps] = splitFieldRootProps(props);
  return (
    <FieldRoot {...fieldRootProps}>
      <FieldMoney {...elementProps} />
    </FieldRoot>
  );
}

/// Time

function FieldTime(props: Omit<ComponentProps<typeof TimeInput>, "id">) {
  const field = useField();

  return (
    <TimeInput
      id={makeInputId(field.idBase)}
      aria-describedby={field.errorId()}
      aria-invalid={Boolean(field.errorId())}
      {...props}
    />
  );
}

export function TimeField(props: WithFieldRootProps<ComponentProps<typeof FieldTime>>) {
  const [fieldRootProps, elementProps] = splitFieldRootProps(props);
  return (
    <FieldRoot {...fieldRootProps}>
      <FieldTime {...elementProps} />
    </FieldRoot>
  );
}

/// Date

function FieldDate(props: Omit<ComponentProps<typeof DateInput>, "id">) {
  const field = useField();

  return (
    <DateInput
      id={makeInputId(field.idBase)}
      aria-describedby={field.errorId()}
      aria-invalid={Boolean(field.errorId())}
      {...props}
    />
  );
}

export function DateField(props: WithFieldRootProps<ComponentProps<typeof FieldDate>>) {
  const [fieldRootProps, elementProps] = splitFieldRootProps(props);
  return (
    <FieldRoot {...fieldRootProps}>
      <FieldDate {...elementProps} />
    </FieldRoot>
  );
}

/// Textarea

function FieldTextarea(
  _props: Omit<ComponentProps<typeof Textarea>, "id"> & {
    width?: FieldWidth;
  },
) {
  const [props, textareaProps] = splitProps(_props, ["width"]);
  const field = useField();
  const form = useForm();
  const width = () => props.width ?? form.fieldWidth;

  return (
    <Textarea
      id={makeInputId(field.idBase)}
      width={width()}
      aria-describedby={field.errorId()}
      aria-invalid={Boolean(field.errorId())}
      {...textareaProps}
    />
  );
}

export function TextAreaField(props: WithFieldRootProps<ComponentProps<typeof FieldTextarea>>) {
  const [fieldRootProps, elementProps] = splitFieldRootProps(props);
  return (
    <FieldRoot {...fieldRootProps}>
      <FieldTextarea {...elementProps} />
    </FieldRoot>
  );
}

/// Select

function FieldSelect(_props: Omit<ComponentProps<typeof Select>, "id"> & { width?: FieldWidth }) {
  const [props, inputProps] = splitProps(_props, ["width", "class"]);
  const field = useField();
  const form = useForm();
  const width = () => props.width ?? form.fieldWidth;

  return (
    <Select
      id={makeInputId(field.idBase)}
      class={clsx(width() === "full" && "w-full", props.class)}
      aria-describedby={field.errorId()}
      aria-invalid={Boolean(field.errorId())}
      {...inputProps}
    />
  );
}

export function SelectField(props: WithFieldRootProps<ComponentProps<typeof FieldSelect>>) {
  const [fieldRootProps, elementProps] = splitFieldRootProps(props);
  return (
    <FieldRoot {...fieldRootProps}>
      <FieldSelect
        {...elementProps}
        class={clsx(elementProps.class, fieldRootProps.needed && NEEDED_RING)}
      />
    </FieldRoot>
  );
}

/// Uploader

function FieldUploader(props: {
  accept?: string;
  multiple?: boolean;
  files: File[];
  onFiles: (files: File[]) => void;
  onRemove: (index: number) => void;
  // Already-stored files (the current value), shown above the dropzone; pass onRemoveExisting to allow
  // clearing them. Uploading a new file is how a single-file field is replaced.
  existing?: ExistingFile[];
  onRemoveExisting?: (index: number) => void;
}) {
  const field = useField();

  return (
    <div class="flex flex-col gap-2">
      <Show when={props.existing?.length}>
        <ExistingFiles files={props.existing ?? []} onRemove={props.onRemoveExisting} />
      </Show>
      <FileDropzone
        id={makeInputId(field.idBase)}
        accept={props.accept}
        multiple={props.multiple}
        onFiles={props.onFiles}
      />
      <Show when={props.files.length > 0}>
        <SelectedFiles files={props.files} onRemove={props.onRemove} />
      </Show>
    </div>
  );
}

export function UploaderField(props: WithFieldRootProps<ComponentProps<typeof FieldUploader>>) {
  const [fieldRootProps, elementProps] = splitFieldRootProps(props);
  return (
    <FieldRoot {...fieldRootProps}>
      <FieldUploader {...elementProps} />
    </FieldRoot>
  );
}

/// Radio

function FieldRadio(
  _props: Omit<ComponentProps<typeof Radio>, "id" | "children"> & { label: string },
) {
  const [props, radioProps] = splitProps(_props, ["label"]);
  const field = useField();
  const id = makeInputId(field.idBase);

  return (
    <Radio id={id} {...radioProps}>
      <RadioLabel for={id}>{props.label}</RadioLabel>
    </Radio>
  );
}

export function RadioField(
  props: Omit<ComponentProps<typeof Radio>, "id"> & {
    label: string;
    checked: boolean;
    errors: FieldError[] | undefined;
  },
) {
  return (
    <Field>
      <FieldRadio {...props} />

      <Show when={props.errors}>
        <FieldErrors>
          <For each={props.errors}>{(error) => <FieldError>{error}</FieldError>}</For>
        </FieldErrors>
      </Show>
    </Field>
  );
}

export function RadioGroupField(
  props: WithFieldRootProps<Omit<ComponentProps<typeof RadioGroup>, "label">>,
) {
  const [fieldRootProps, radioGroupProps] = splitFieldRootProps(props);

  return (
    <FieldRoot {...fieldRootProps}>
      <RadioGroup
        label={groupFieldLabel(RadioGroupLabel, fieldRootProps.label, fieldRootProps.required)}
        {...radioGroupProps}
      />
    </FieldRoot>
  );
}

export function RadioGroupFieldOption(props: ComponentProps<typeof RadioGroupOption>) {
  return <RadioGroupOption {...props} />;
}

function groupFieldLabel(
  LabelComponent: (props: ParentProps) => JSX.Element,
  label: string | undefined,
  required: boolean | undefined,
) {
  if (!label) return undefined;

  return (
    <LabelComponent>
      <span class="sr-only">
        <GroupFieldLabelText label={label} required={required} />
      </span>
    </LabelComponent>
  );
}

export function ColorRadioGroupField(
  props: WithFieldRootProps<{
    name: string;
    onChange: (value: string) => void;
    options: Array<{ color: string; label?: string; value: string }>;
    value: string;
    // Adds a swatch that opens the native color picker; a picked color arrives
    // through onChange as its "#rrggbb" hex instead of an option value.
    customizable?: boolean;
  }>,
) {
  const [fieldRootProps, colorRadioGroupProps] = splitFieldRootProps(props);
  const isCustomValue = () =>
    !colorRadioGroupProps.options.some((option) => option.value === colorRadioGroupProps.value);

  return (
    <FieldRoot {...fieldRootProps}>
      <input type="hidden" name={colorRadioGroupProps.name} value={colorRadioGroupProps.value} />
      <div class="flex flex-wrap gap-2">
        <For each={colorRadioGroupProps.options}>
          {(option) => (
            <ColorRadio
              checked={colorRadioGroupProps.value === option.value}
              color={option.color}
              label={option.label}
              onChange={() => colorRadioGroupProps.onChange(option.value)}
            />
          )}
        </For>
        <Show when={colorRadioGroupProps.customizable}>
          <CustomColorRadio
            checked={isCustomValue()}
            value={isCustomValue() ? colorRadioGroupProps.value : null}
            onChange={colorRadioGroupProps.onChange}
          />
          <Show when={isCustomValue()}>
            <CustomHueSlider
              value={colorRadioGroupProps.value}
              onChange={colorRadioGroupProps.onChange}
            />
          </Show>
        </Show>
      </div>
    </FieldRoot>
  );
}

// Custom colors live on a single LCH hue axis with lightness and chroma fixed,
// so every pick lands in one consistent band. The slider drives H; the stored
// value stays an sRGB hex (converted per change). At this L/C the teal-to-blue
// arc (hues ~170-245) exceeds sRGB and gets gamut-clamped: the track and the
// stored hex both show the clamped color (slightly softer, hue read-back off
// by up to ~6 degrees), so the slider never shows a color that isn't stored.
const CUSTOM_COLOR_L = 64;
const CUSTOM_COLOR_C = 50;
const DEFAULT_CUSTOM_HUE = 250;

const HUE_STOPS = Array.from({ length: 13 }, (_, i) =>
  lchToHex(CUSTOM_COLOR_L, CUSTOM_COLOR_C, i * 30),
);

function CustomColorRadio(props: {
  checked: boolean;
  value: string | null;
  onChange: (value: string) => void;
}) {
  return (
    <button
      type="button"
      title="Custom color"
      class="flex cursor-pointer items-center gap-1.5"
      onClick={() => {
        // Clicking while a preset is active selects custom right away, seeded
        // at the default hue; the slider below then adjusts it.
        if (!props.checked) {
          props.onChange(lchToHex(CUSTOM_COLOR_L, CUSTOM_COLOR_C, DEFAULT_CUSTOM_HUE));
        }
      }}
    >
      <span class="text-sm text-typography-secondary">Custom</span>
      <span
        class="inline-block size-8 rounded-full border-2 transition-transform hover:scale-110"
        classList={{
          "border-brand-primary": props.checked,
          "border-transparent": !props.checked,
        }}
        style={
          props.checked && props.value
            ? { "background-color": props.value }
            : { background: `conic-gradient(${HUE_STOPS.join(", ")})` }
        }
      />
    </button>
  );
}

function CustomHueSlider(props: { value: string; onChange: (value: string) => void }) {
  // Reading the hue back from the stored hex keeps the thumb honest for any
  // custom value, including ones picked before the slider existed.
  const hue = () => Math.round(hexToLch(props.value).h);

  return (
    <input
      type="range"
      min="0"
      max="360"
      step="1"
      aria-label="Custom color hue"
      value={hue()}
      onInput={(e) =>
        props.onChange(lchToHex(CUSTOM_COLOR_L, CUSTOM_COLOR_C, e.currentTarget.valueAsNumber))
      }
      class="h-3 w-40 cursor-pointer appearance-none self-center rounded-full [&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:[background:var(--thumb-color)] [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:[background:var(--thumb-color)]"
      style={{
        background: `linear-gradient(to right, ${HUE_STOPS.join(", ")})`,
        "--thumb-color": props.value,
      }}
    />
  );
}

function GroupFieldLabelText(props: { label: string; required?: boolean }) {
  return (
    <span class="inline-flex items-center gap-1">
      <span>{props.label}</span>
      <Show when={props.required}>
        <span>*</span>
      </Show>
    </span>
  );
}

/// Toggle

/// Toggle

// The field's id and error wiring go on the checkbox itself: without them the FieldLabel's `for`
// pointed at an id nothing owned, so the label did not focus or name the toggle, and a field error
// was visible text the checkbox never announced.
function FieldToggle(props: Omit<ComponentProps<typeof Toggle>, "label">) {
  const field = useField();

  return (
    <Toggle
      id={makeInputId(field.idBase)}
      aria-describedby={field.errorId()}
      aria-invalid={Boolean(field.errorId())}
      {...props}
    />
  );
}

export function ToggleField(
  props: WithFieldRootProps<Omit<ComponentProps<typeof Toggle>, "label">>,
) {
  const [fieldRootProps, toggleProps] = splitFieldRootProps(props);

  return (
    <FieldRoot {...fieldRootProps}>
      <FieldToggle {...toggleProps} />
    </FieldRoot>
  );
}

/// Combobox

function FieldCombobox<TItem, TValue>(
  _props: ComboboxProps<TItem, TValue> & {
    width?: FieldWidth;
  },
) {
  const [props, comboboxProps] = splitProps(_props, ["width", "class"]);
  const form = useForm();
  const field = useField();
  const width = () => props.width ?? form.fieldWidth;

  return (
    <Combobox
      inputId={makeInputId(field.idBase)}
      class={clsx(width() === "full" && "w-full", props.class)}
      {...(comboboxProps as ComboboxProps<TItem, TValue>)}
    />
  );
}

export type ComboboxFieldProps<TItem, TValue> = WithFieldRootProps<
  ComponentProps<typeof FieldCombobox<TItem, TValue>>
>;
export function ComboboxField<TItem, TValue>(props: ComboboxFieldProps<TItem, TValue>) {
  const [fieldRootProps, elementProps] = splitFieldRootProps(props);
  return (
    <FieldRoot {...fieldRootProps}>
      <FieldCombobox {...elementProps} />
    </FieldRoot>
  );
}

// Utils

export function clearNestedErrors<TForm extends Record<string, unknown>>(
  form: InertiaForm<TForm>,
  path: string,
) {
  Object.keys(form.errors).forEach((key) => {
    if (key.startsWith(path)) {
      form.clearErrors(key);
    }
  });
}

export function moveNestedErrors<TForm extends Record<string, unknown>>(
  form: InertiaForm<TForm>,
  sourcePath: keyof FormDataErrors<TForm>,
  destinationPath: keyof FormDataErrors<TForm>,
) {
  Object.entries(form.errors).forEach(([key, errors]) => {
    if (key.startsWith(sourcePath)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form.setError(key.replace(sourcePath, destinationPath) as any, errors as string[]);
    }
  });
  clearNestedErrors(form, sourcePath);
}

export function getNestedErrors<TForm extends Record<string, unknown>>(
  form: InertiaForm<TForm>,
  path: keyof FormDataErrors<TForm>,
) {
  return Object.fromEntries(Object.entries(form.errors).filter(([key]) => key.startsWith(path)));
}

export function hasNestedErrors<TForm extends Record<string, unknown>>(
  form: InertiaForm<TForm>,
  path: keyof FormDataErrors<TForm>,
) {
  const errors = getNestedErrors(form, path);
  return Object.keys(errors).length > 0;
}
